import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setNotification } from "features/Notification/notificationSlice";
import authHeader from "services/auth-header";
import API from "services/axios";
import { v4 as uuid } from "uuid";

export default function Security() {
  const user = useSelector((state) => state.user.userInfo);

  /*   All States
   ********************************************* */
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [selectedIp, setSelectedIp] = useState(null);
  const [ipList, setIpList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enableIpCheck, setEnableIpCheck] = useState(null);
  const [newIpForm, setNewIpForm] = useState({
    ip: "",
    name: "",
  });

  /*   All Functions
   ********************************************* */
  const dispatch = useDispatch();

  // Fetch IPs from database
  const fetchIps = async () => {
    try {
      setLoading(true);
      const { data } = await API.get(
        "/company-ips",

        {
          headers: authHeader(),
        }
      );
      if (data.success) {
        setIpList(data.data);
        setEnableIpCheck(data.enableIpCheck);
      } else {
        dispatch(
          setNotification({
            message: "Failed to fetch IP list",
            type: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error fetching IPs:", error);
      dispatch(
        setNotification({
          message: error.response?.data?.message || "An error occurred",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // update company enableIpCheck
  const updateCompanyEnableIpCheck = async (enable) => {
    try {
      setLoading(true);
      // use /companies/update/:id
      const { data } = await API.post(
        `/companies/update/` + user?.companyId,
        { enableIpCheck: enable },
        {
          headers: authHeader(),
        }
      );

      if (data.success) {
        setEnableIpCheck(enable);
        dispatch(
          setNotification({
            message: `IP Check ${enable ? "enabled" : "disabled"} successfully`,
            type: "success",
          })
        );
      } else {
        dispatch(
          setNotification({
            message: data.message || "Failed to update IP Check",
            type: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error updating IP Check:", error);
      dispatch(
        setNotification({
          message: error.response?.data?.message || "An error occurred",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleIpFormChange = (e) => {
    let id = e.target.id;
    let name = e.target.name;
    let value = e.target.value;

    setNewIpForm((prev) => {
      return { ...prev, [name]: value, id };
    });
  };

  // Add / Update Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isUpdateMode) {
      updateIp(selectedIp);
    } else {
      addIp();
    }
  };

  const addIp = async () => {
    try {
      setLoading(true);
      let { ip, name } = newIpForm;

      if (!ip) {
        dispatch(
          setNotification({
            message: "Please enter an IP address",
            type: "error",
          })
        );
        setLoading(false);
        return;
      }

      // Format data for API
      const ipData = {
        ip,
        name: name || "",
        companyId: null, // This will be set by the backend based on the user's company
      };

      const { data } = await API.post("/company-ip", ipData, {
        headers: authHeader(),
      });

      if (data.success) {
        dispatch(
          setNotification({
            message: "IP added successfully",
            type: "success",
          })
        );
        fetchIps(); // Refresh the list
        setNewIpForm({ ip: "", name: "" });
      } else {
        dispatch(
          setNotification({
            message: data.message || "Failed to add IP",
            type: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error adding IP:", error);
      dispatch(
        setNotification({
          message: error.response?.data?.message || "An error occurred",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const updateIp = async (ip) => {
    try {
      setLoading(true);
      let { ip: ipAddress, name } = newIpForm;

      if (!ipAddress) {
        dispatch(
          setNotification({
            message: "Please enter an IP address",
            type: "error",
          })
        );
        setLoading(false);
        return;
      }

      // Format data for API
      const ipData = {
        id: ip.id,
        ip: ipAddress,
        name: name || "",
      };

      const { data } = await API.patch("/company-ip", ipData, {
        headers: authHeader(),
      });

      if (data.success) {
        dispatch(
          setNotification({
            message: "IP updated successfully",
            type: "success",
          })
        );
        fetchIps(); // Refresh the list
        setIsUpdateMode(false);
        setNewIpForm({ ip: "", name: "" });
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to update IP",
            type: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error updating IP:", error);
      dispatch(
        setNotification({
          message: error.response?.data?.message || "An error occurred",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteIp = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete(`/company-ip/${id}`, {
        headers: authHeader(),
      });

      if (data.success) {
        dispatch(
          setNotification({
            message: "IP deleted successfully",
            type: "success",
          })
        );
        fetchIps(); // Refresh the list
      } else {
        dispatch(
          setNotification({
            message: "Failed to delete IP",
            type: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error deleting IP:", error);
      dispatch(
        setNotification({
          message: error.response?.data?.message || "An error occurred",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      fetchIps();
    }, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12 px-0 py-0">
          <div className="card bg-white h-100 shadow-none">
            <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Restrict Company Users to these Ips</h5>
              <div className="d-flex align-items-center gap-2">
                <label htmlFor="enable">Enable IP Whitelisting</label>
                <div className="d-flex align-items-center">
                  <input
                    type="checkbox"
                    id="switch2"
                    checked={enableIpCheck}
                    onChange={(e) => {
                      updateCompanyEnableIpCheck(e.target.checked);
                    }}
                    data-switch="primary"
                  />
                  <label
                    for="switch2"
                    data-on-label="On"
                    data-off-label="Off"
                  ></label>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div>
                <form className="pt-2">
                  <div className="d-flex gap-1 align-items-center">
                    <input
                      onChange={handleIpFormChange}
                      type="text"
                      name="name"
                      id={newIpForm?.id || uuid()}
                      placeholder="Enter Device Name (optional)"
                      value={newIpForm?.name || ""}
                      className="form-control"
                    />
                    <input
                      onChange={handleIpFormChange}
                      type="text"
                      placeholder="Enter IP Address"
                      name="ip"
                      value={newIpForm?.ip || ""}
                      id={newIpForm?.id || uuid()}
                      className="form-control"
                    />
                    <input
                      type="submit"
                      value={isUpdateMode ? "Update" : "ADD"}
                      onClick={handleSubmit}
                      className="btn btn-success"
                    />
                    {isUpdateMode && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setNewIpForm({ name: "", ip: "" });
                          setIsUpdateMode(false);
                        }}
                        className="btn btn-danger"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </form>
              </div>
              <hr />

              {loading ? (
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    overflowY: "auto",
                    height: "50vh",
                  }}
                >
                  <table className="table">
                    <thead>
                      <tr className="sticky-top bg-white custom-shadow-sm">
                        <th scope="col" className="w-40 fw-bold">
                          Location Name
                        </th>
                        <th scope="col" className="w-40 fw-bold">
                          IP Address
                        </th>
                        <th scope="col" className="w-20 fw-bold">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ipList?.map((ip) => (
                        <tr key={ip.id}>
                          <td>{ip?.name || "-"}</td>
                          <td>{ip?.ip}</td>
                          <td>
                            <div className="d-flex">
                              <button
                                onClick={() => {
                                  setIsUpdateMode(true);
                                  setSelectedIp(ip);
                                  setNewIpForm({
                                    ip: ip.ip,
                                    name: ip.name || "",
                                    id: ip.id,
                                  });
                                }}
                                className="btn btn-info btn-sm"
                                type="button"
                              >
                                <i className="mdi mdi-pencil"></i>
                              </button>
                              <button
                                onClick={() => deleteIp(ip.id)}
                                className="btn btn-danger btn-sm ms-1"
                                type="button"
                              >
                                <i className="mdi mdi-delete"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
